import { Inject } from '@odinjs/odin';

import Controller, { OnBeforeFetch, OnBeforeSubmit, OnClear, OnClick, OnMount, OnSubmit, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { AutocompleteAPI, FetchAPI, FilterAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';

import { ApplicationRoute } from '../../../constants/routes';
import { DateFilterUtils } from './../../../utils/date-filter-utils';
import BaseController from '../../BaseController';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { VitalSignListAliases } from '../../../codegen';
import ShowToast from '../../../utils/toast-service';
import { VitalSignRootService } from '../vital-sign-respiratory-monitoring/vital-sign-root-service';
import { ItemPepType, RulesItemConfiguration } from '../../../constants/utils-pep';
import { Endpoint } from '../../../constants/endpoints';
import { YesNoType } from '../../../utils/enum-utils';

import './vital-sign-list-controller.css';

interface FilterParam {
  dtFinal: Date;
  dtInicial: Date;
}
@Controller({ component: 'layout-vital-sign-list', fragment: 'vital-sign-list' })
class VitalSignListController extends BaseController {

  dtInicial!: string;
  dtFinal!: string;

  @Inject(VitalSignListAliases.FETCH_VITAL_SIGN)
  private fetchDataApi!: FetchAPI;

  @Inject(VitalSignListAliases.AUTOC_VITAL_SIGN_NM_ATTR)
  private autoCompleteNmAttr!: AutocompleteAPI;

  @Inject(VitalSignListAliases.PANEL_GLOBAL_ACTIONS)
  private panelGlobalActionsApi!: PanelAPI;

  internalNavigation!: boolean;

  @OnMount({ target: VitalSignListAliases.LAYOUT_VITAL_SIGN_LIST })
  handleMount(): void {
    InformationBarUtils.collapsedBar = true;

    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    this.context.set({
      encounterId: encounterId,
      patientId: patientId,
    });

    this.internalNavigation = false;

    this.checkItemEditPermission()
      .finally(() => {
        const allowInfo = InformationBarUtils.allowInfo && VitalSignRootService.isAllowEdit;
        this.panelGlobalActionsApi.setVisible(allowInfo);
      })
  }

  @OnUnmount({ target: VitalSignListAliases.LAYOUT_VITAL_SIGN_LIST })
  handleUnmount(): void {
    InformationBarUtils.collapsedBar = false;
  }

  @OnMount({ target: VitalSignListAliases.FILTER_VITAL_SIGNS })
  handleFilterMount(event: MountEvent): void {
    const {
      lastMonth,
      currentDate
    } = DateFilterUtils.getDateRange();

    const record = {
      dtFinal: currentDate,
      dtInicial: lastMonth,
    };

    const filter = event.target as FilterAPI;
    const restored = filter.restoreState(true);

    if (restored) {
      Object.assign(record, filter.getRecord());
    }

    filter.setData(record);
    filter.submit();

  }

  @OnBeforeSubmit({ target: VitalSignListAliases.FILTER_VITAL_SIGNS })
  handleBeforeSubmit(event: SubmitEvent) : void {
    const dateLimit = 90;
    const params = {
      dtFinal: event.record.dtFinal,
      dtInicial: event.record.dtInicial,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }

    if(event.record.nmAttr) {
      this.fetchDataApi.setParameters({...params, nmAttr: event.record.nmAttr});
      return;
    }

    if(DateFilterUtils.diffDate(params.dtFinal, params.dtInicial) > dateLimit) {
      ShowToast.open(MessageService.translate('text.message.more-ninety-days'), ToastLevel.CAUTION);
    }

    this.fetchDataApi.setParameters(params);
  }

  @OnSubmit({ target: VitalSignListAliases.FILTER_VITAL_SIGNS })
  handleClickApply(event: SubmitEvent): void {
    const dateLimit = 90;
    const params = {
      dtFinal: event.record.dtFinal,
      dtInicial: event.record.dtInicial,
    }

    if(DateFilterUtils.diffDate(params.dtFinal, params.dtInicial) > dateLimit) {
      ShowToast.open(MessageService.translate('text.message.more-ninety-days'), ToastLevel.CAUTION);
    }

  }

  @OnBeforeFetch(VitalSignListAliases.FETCH_VITAL_SIGN)
  handleBeforeFetch(event: FetchEvent): void {
    const fetchApi = event.target as FetchAPI;
    const params = fetchApi.getParameters();
    fetchApi.setParameters({
      ...params,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    })
  }

  @OnUnmount({ target: VitalSignListAliases.FILTER_VITAL_SIGNS })
  handleFilterUnmount(e: MountEvent): void {
    const filter = e.target as FilterAPI;

    if ( this.internalNavigation ) {

      const record = filter.getRecord() as Record<string, FilterParam>;
      filter.setData(record);

      filter.persistState();

    } else {

      filter.clearPersistedState();

    }
  }

  @OnClick({ target: VitalSignListAliases.DECK_VITAL_SIGN })
  handleCardClick(event: ClickEvent): void {
    this.internalNavigation = true;

    const { vitalSign } = event.value;
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const { dtLiberacao, nrSinalVital: vitalSignId } = vitalSign;
    const isDetails = 'S';
    const allowInfo = InformationBarUtils.allowInfo && VitalSignRootService.isAllowEdit;
    const accessViewer = dtLiberacao || !allowInfo;
    const route = accessViewer ? ApplicationRoute.VITAL_SIGN_VIEWER : ApplicationRoute.VITAL_SIGN_EDIT;
    const params = accessViewer ? { encounterId, isDetails, vitalSignId } : { encounterId, vitalSignId };

    applicationContext.redirect(ApplicationRoute.ELECTRONIC_PATIENT_CHART +
      ApplicationRoute.PATIENT_LIST +
      ApplicationRoute.VITAL_SIGN_LIST +
      route, params);
  }

  @OnClick({ target: VitalSignListAliases.BUTTON_ADD_VITAL_SIGNAL })
  handleAddVitalSignClick(): void {
    applicationContext.redirect(ApplicationRoute.ELECTRONIC_PATIENT_CHART +
      ApplicationRoute.PATIENT_LIST +
      ApplicationRoute.VITAL_SIGN_LIST +
      ApplicationRoute.VITAL_SIGN_CREATE);
  }

  @OnClear({ target: VitalSignListAliases.FILTER_VITAL_SIGNS })
  handleFitlerClear(): void {
    this.dtInicial = '';
    this.dtFinal = '';
    this.autoCompleteNmAttr.setValue({} as Record<string, Value>)
  }

  private checkItemEditPermission(): Promise<void> {
    const itemType = ItemPepType.VITAL_SIGN;
    return HttpService.get(Endpoint.PEP_CAN_EDIT_ITEM, { itemType })
      .then(response => {
        const configuration = response.body as RulesItemConfiguration;
        const isAllowEdit = configuration?.allowEditing === YesNoType.YES;
        VitalSignRootService.isAllowEdit = isAllowEdit;
      })
  }
}

export default VitalSignListController;
